import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  NewspaperIcon,
  CodeBracketIcon,
  UserGroupIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

interface RouteConfig {
  title: string;
  tagline: string;
  icon: React.ReactNode;
}

const routeConfig: Record<string, RouteConfig> = {
  '/docs': {
    title: 'Documentation',
    tagline: 'Our scribes are writing furiously...',
    icon: <DocumentTextIcon className="w-10 h-10" />,
  },
  '/help': {
    title: 'Help Center',
    tagline: 'Answers are being compiled...',
    icon: <QuestionMarkCircleIcon className="w-10 h-10" />,
  },
  '/blog': {
    title: 'Blog',
    tagline: 'Stories are being penned...',
    icon: <NewspaperIcon className="w-10 h-10" />,
  },
  '/api': {
    title: 'API Docs',
    tagline: 'The codex is being prepared...',
    icon: <CodeBracketIcon className="w-10 h-10" />,
  },
  '/about': {
    title: 'About Us',
    tagline: 'Our story is being written...',
    icon: <UserGroupIcon className="w-10 h-10" />,
  },
  '/careers': {
    title: 'Careers',
    tagline: 'New chapters opening soon...',
    icon: <BriefcaseIcon className="w-10 h-10" />,
  },
  '/contact': {
    title: 'Contact',
    tagline: 'Setting up the messenger pigeons...',
    icon: <EnvelopeIcon className="w-10 h-10" />,
  },
  '/press': {
    title: 'Press',
    tagline: 'Headlines in the making...',
    icon: <MegaphoneIcon className="w-10 h-10" />,
  },
  '/privacy': {
    title: 'Privacy Policy',
    tagline: 'Sealing the vault...',
    icon: <ShieldCheckIcon className="w-10 h-10" />,
  },
  '/terms': {
    title: 'Terms of Service',
    tagline: 'The fine print is being refined...',
    icon: <DocumentCheckIcon className="w-10 h-10" />,
  },
  '/cookies': {
    title: 'Cookie Policy',
    tagline: 'Baking the details...',
    icon: <SparklesIcon className="w-10 h-10" />,
  },
  '/pricing': {
    title: 'Pricing',
    tagline: 'Calculating the perfect numbers...',
    icon: <CurrencyDollarIcon className="w-10 h-10" />,
  },
};

const defaultConfig: RouteConfig = {
  title: 'Coming Soon',
  tagline: 'Something amazing is being crafted...',
  icon: <SparklesIcon className="w-10 h-10" />,
};

// Knowledge Graph Node Component
const KnowledgeNode: React.FC<{
  x: string;
  y: string;
  size: number;
  delay: number;
  growing?: boolean;
}> = ({ x, y, size, delay, growing }) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      left: x,
      top: y,
      width: size,
      height: size,
      background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.4) 0%, rgba(59, 130, 246, 0.4) 100%)',
      boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)',
    }}
    initial={growing ? { scale: 0, opacity: 0 } : undefined}
    animate={{
      scale: growing ? [0, 1.2, 1] : [1, 1.15, 1],
      opacity: growing ? [0, 1, 0.8] : [0.5, 0.8, 0.5],
    }}
    transition={{
      duration: growing ? 2 : 4,
      repeat: growing ? 0 : Infinity,
      delay,
      ease: 'easeOut',
    }}
  />
);

// Animated Pen/Pencil Component
const WritingPen: React.FC = () => (
  <motion.div
    className="relative"
    animate={{
      x: [0, 8, 0, -8, 0],
      y: [0, 2, 0, 2, 0],
      rotate: [0, 5, 0, -5, 0],
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  >
    <div
      className="p-4 rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
        border: '1px solid rgba(6, 182, 212, 0.3)',
      }}
    >
      <PencilIcon className="w-10 h-10 text-cyan-400" />
    </div>
    {/* Writing line trail */}
    <motion.div
      className="absolute -bottom-2 left-1/2 w-8 h-0.5 rounded-full"
      style={{
        background: 'linear-gradient(90deg, transparent, #06B6D4, transparent)',
        marginLeft: '-16px',
      }}
      animate={{
        scaleX: [0, 1, 0],
        opacity: [0, 0.6, 0],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  </motion.div>
);

// Floating Book Component
const FloatingBook: React.FC<{ x: string; y: string; delay: number; size: number }> = ({
  x,
  y,
  delay,
  size,
}) => (
  <motion.div
    className="absolute"
    style={{ left: x, top: y }}
    animate={{
      y: [0, -10, 0],
      rotate: [-5, 5, -5],
    }}
    transition={{
      duration: 5,
      repeat: Infinity,
      delay,
      ease: 'easeInOut',
    }}
  >
    <svg
      width={size}
      height={size * 0.8}
      viewBox="0 0 50 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Book cover */}
      <rect
        x="5"
        y="5"
        width="40"
        height="30"
        rx="2"
        fill="rgba(6, 182, 212, 0.15)"
        stroke="rgba(6, 182, 212, 0.3)"
        strokeWidth="1"
      />
      {/* Book spine */}
      <line x1="25" y1="5" x2="25" y2="35" stroke="rgba(6, 182, 212, 0.3)" strokeWidth="1" />
      {/* Pages */}
      <rect x="8" y="8" width="14" height="2" rx="1" fill="rgba(148, 163, 184, 0.2)" />
      <rect x="8" y="12" width="12" height="2" rx="1" fill="rgba(148, 163, 184, 0.2)" />
      <rect x="28" y="8" width="14" height="2" rx="1" fill="rgba(148, 163, 184, 0.2)" />
      <rect x="28" y="12" width="12" height="2" rx="1" fill="rgba(148, 163, 184, 0.2)" />
    </svg>
  </motion.div>
);

const ComingSoon: React.FC = () => {
  const location = useLocation();
  const config = routeConfig[location.pathname] || defaultConfig;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 relative overflow-hidden">
      {/* Knowledge Graph Background - Growing nodes */}
      <div className="absolute inset-0">
        <KnowledgeNode x="12%" y="25%" size={10} delay={0} />
        <KnowledgeNode x="82%" y="18%" size={8} delay={0.3} />
        <KnowledgeNode x="78%" y="68%" size={12} delay={0.6} />
        <KnowledgeNode x="18%" y="72%" size={10} delay={0.9} />
        <KnowledgeNode x="45%" y="12%" size={6} delay={1.2} />
        <KnowledgeNode x="88%" y="42%" size={8} delay={1.5} />
        {/* Growing nodes - appear and stay */}
        <KnowledgeNode x="35%" y="30%" size={6} delay={2} growing />
        <KnowledgeNode x="60%" y="65%" size={8} delay={3} growing />
        <KnowledgeNode x="25%" y="50%" size={5} delay={4} growing />
      </div>

      {/* Floating Books */}
      <FloatingBook x="8%" y="20%" delay={0} size={45} />
      <FloatingBook x="85%" y="25%" delay={1} size={40} />
      <FloatingBook x="10%" y="65%" delay={2} size={35} />
      <FloatingBook x="80%" y="60%" delay={3} size={50} />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-white" />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-xl w-full"
      >
        <div
          className="text-center p-8 md:p-12 rounded-3xl border border-gray-200"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Coming Soon Badge */}
          <motion.div
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
          >
            <SparklesIcon className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">Coming Soon</span>
          </motion.div>

          {/* Writing Animation */}
          <div className="flex justify-center mb-6">
            <WritingPen />
          </div>

          {/* Route Icon + Title */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="flex items-center justify-center gap-3 mb-3"
          >
            <span className="text-cyan-400">{config.icon}</span>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{config.title}</h1>
          </motion.div>

          {/* Witty Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-gray-600 text-lg md:text-xl mb-6"
          >
            {config.tagline}
          </motion.p>

          {/* Subtle message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mb-8 py-3 px-4 rounded-xl bg-gray-100/50 border border-gray-200/50"
          >
            <p className="text-gray-500 text-sm">
              We're crafting something special. Check back soon!
            </p>
          </motion.div>

          {/* Back to Home CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all"
              style={{
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 50px rgba(6, 182, 212, 0.5)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back to Home
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default ComingSoon;
